/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../theme';
import { 
  LogIn, 
  Mail, 
  Lock, 
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { ROUTES } from '../routes';
import { authService } from '../services/authService';

const Login: React.FC = () => {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await authService.login({
        email,
        password,
      });

      const { accessToken, user } = response.data.data;
      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('userId', user.id.toString());
      window.location.href = ROUTES.DASHBOARD;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center p-4 animate-in fade-in duration-700">
      <div className="w-full max-w-md space-y-8 p-10 rounded-3xl border shadow-2xl relative overflow-hidden" 
           style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
        
        {/* Decorative elements */}
        <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-5 blur-3xl" style={{ backgroundColor: theme.colors.primary }}></div>
        <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full opacity-5 blur-3xl" style={{ backgroundColor: theme.colors.primary }}></div>

        <div className="text-center relative z-10">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-500/20" 
               style={{ backgroundColor: theme.colors.primary }}>
            <LogIn size={32} className="text-white" />
          </div>
          <h2 className="text-3xl font-black tracking-tight" style={{ color: theme.colors.text }}>Welcome Back</h2>
          <p className="mt-2 text-sm font-bold" style={{ color: theme.colors.mutedText }}>
            Sign in to manage your local marketplace
          </p>
        </div>
        
        {error && (
          <div className="p-4 rounded-xl border text-xs font-bold flex items-center gap-2 animate-in shake duration-300" 
               style={{ backgroundColor: theme.colors.danger + '10', borderColor: theme.colors.danger, color: theme.colors.danger }}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-6 relative z-10">
          <div className="space-y-1.5">
            <label className="text-xs font-black uppercase tracking-wider ml-1 flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
              <Mail size={14} /> Email Address
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
              style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex justify-between items-center px-1">
              <label className="text-xs font-black uppercase tracking-wider flex items-center gap-1.5" style={{ color: theme.colors.mutedText }}>
                <Lock size={14} /> Password
              </label>
              <Link to="#" className="text-[10px] font-black uppercase tracking-widest hover:opacity-70" style={{ color: theme.colors.primary }}>
                Forgot?
              </Link>
            </div>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all"
              style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-2xl font-black text-white transition-all hover:opacity-90 shadow-xl flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: theme.colors.primary }}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                Sign In <ArrowRight size={20} />
              </>
            )}
          </button>
        </form>

        <div className="pt-6 border-t text-center relative z-10" style={{ borderColor: theme.colors.border }}>
          <p className="text-sm font-bold" style={{ color: theme.colors.mutedText }}>
            New to Hometown Market?{' '}
            <Link to={ROUTES.SIGNUP} className="font-black hover:opacity-70 transition-opacity" style={{ color: theme.colors.primary }}>
              Create Account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
